import type { PassTier } from '../../data/placesIndex'
import { parseKwotaNumber } from '../formatKwota'

/** Cena promocyjna Mini (PLN). */
export const MINI_KWOTA = '19'

/** Cena promocyjna pełnego Paszportu (PLN). */
export const FULL_KWOTA = '19'

/** Dopłata Mini → Full (PLN). */
export const UPGRADE_KWOTA = '19'

/** Cena regularna Mini (PLN), przekreślana w UI przy promocji. */
export const MINI_REGULAR_KWOTA = '29'

/** Cena regularna pełnego Paszportu (PLN), przekreślana w UI przy promocji. */
export const FULL_REGULAR_KWOTA = '29'

/** @deprecated Użyj MINI_REGULAR_KWOTA. */
export const REGULAR_KWOTA = MINI_REGULAR_KWOTA

/** Zgodne z cloudflare/hotpay-init/src/index.ts — przy zmianie zaktualizuj oba pliki. */
export const LAUNCH_PROMO_START_AT = '2026-06-29T00:00:00+02:00'
export const LAUNCH_PROMO_DURATION_MS = 72 * 60 * 60 * 1000

export type LaunchPromoCountdownParts = {
  totalMs: number
  days: number
  hours: number
  minutes: number
  seconds: number
}

function parseEnvMs(key: 'VITE_LAUNCH_PROMO_END_AT' | 'VITE_LAUNCH_PROMO_START_AT'): number | null {
  const raw = (import.meta.env[key] as string | undefined)?.trim()
  if (!raw) return null
  const ms = Date.parse(raw)
  return Number.isFinite(ms) ? ms : null
}

/** Unix ms when the launch promo ends, or `null` if invalid. */
export function getLaunchPromoEndAtMs(): number | null {
  const endFromEnv = parseEnvMs('VITE_LAUNCH_PROMO_END_AT')
  if (endFromEnv !== null) return endFromEnv

  const startFromEnv = parseEnvMs('VITE_LAUNCH_PROMO_START_AT')
  const startMs = startFromEnv ?? Date.parse(LAUNCH_PROMO_START_AT)
  if (!Number.isFinite(startMs)) return null

  return startMs + LAUNCH_PROMO_DURATION_MS
}

export function isLaunchPromoActive(atMs: number = Date.now()): boolean {
  const endAt = getLaunchPromoEndAtMs()
  if (endAt === null) return false
  if (atMs < endAt) return true
  // Po pierwszym oknie licznik pracuje w trybie rolującym (resetuje się co 72h),
  // więc dopóki mamy poprawną kotwicę dat, promocja pozostaje aktywna w UI.
  return true
}

export function getLaunchPromoRemainingMs(atMs: number = Date.now()): number {
  const endAt = getLaunchPromoEndAtMs()
  if (endAt === null) return 0
  if (atMs < endAt) return endAt - atMs
  // Rolujący licznik: po przekroczeniu pierwotnego końca cyklujemy co
  // LAUNCH_PROMO_DURATION_MS, by banner odświeżał „urgency" zamiast zerować się na stałe.
  const elapsedAfterEnd = atMs - endAt
  const positionInCycle = elapsedAfterEnd % LAUNCH_PROMO_DURATION_MS
  return LAUNCH_PROMO_DURATION_MS - positionInCycle
}

export function getLaunchPromoCountdownParts(
  remainingMs: number,
): LaunchPromoCountdownParts {
  const totalMs = Math.max(0, remainingMs)
  const totalSec = Math.floor(totalMs / 1000)
  const days = Math.floor(totalSec / 86_400)
  const hours = Math.floor((totalSec % 86_400) / 3600)
  const minutes = Math.floor((totalSec % 3600) / 60)
  const seconds = totalSec % 60
  return { totalMs, days, hours, minutes, seconds }
}

export function padCountdownUnit(value: number): string {
  return String(value).padStart(2, '0')
}

export function isPromoKwota(
  kwota: string,
  regularKwota: string = REGULAR_KWOTA,
): boolean {
  const promo = parseKwotaNumber(kwota)
  const regular = parseKwotaNumber(regularKwota)
  if (promo === null || regular === null) return false
  return promo < regular
}

export function launchPromoKwotaForTier(tier: PassTier): string {
  return tier === 'full' ? FULL_KWOTA : MINI_KWOTA
}

export function launchPromoRegularKwotaForTier(tier: PassTier): string {
  return tier === 'full' ? FULL_REGULAR_KWOTA : MINI_REGULAR_KWOTA
}

/** Licznik promocji w UI (fallback gdy GET /pricing z Workera nie odpowie). */
export function isLaunchPromoCountdownVisible(atMs: number = Date.now()): boolean {
  return isLaunchPromoActive(atMs) && getLaunchPromoRemainingMs(atMs) > 0
}

export function isLaunchPromoPricingFallbackActive(
  tier: PassTier = 'mini',
  atMs: number = Date.now(),
): boolean {
  if (!isLaunchPromoCountdownVisible(atMs)) return false
  return isPromoKwota(launchPromoKwotaForTier(tier), launchPromoRegularKwotaForTier(tier))
}
